/**
 * Standalone routes for the processing console.
 *
 * These render the same console the validation flow uses, driven by a sample
 * run so the layout and animation can be reviewed without uploading a file.
 * The top bar says "Preview" so the figures are never mistaken
 * for a real validation.
 */
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { RotateCcw, ArrowLeft, ChevronRight } from "lucide-react";
import { PageShell } from "../../components/layout/PageShell";
import { type ConsolePhase } from "./processingPipeline";
import { ProcessingConsole } from "./ProcessingConsole";
import { ProcessingConsole3D } from "./ProcessingConsole3D";

function usePreviewRun() {
  const [phase, setPhase] = useState<ConsolePhase>("running");
  const [runKey, setRunKey] = useState(0);

  // Stands in for the upload request the real flow waits on.
  const settle = useCallback(() => setPhase("complete"), []);

  const restart = useCallback(() => {
    setPhase("running");
    setRunKey((k) => k + 1);
  }, []);

  return { phase, runKey, settle, restart };
}

function PreviewControls({
  onSettle,
  onRestart,
  settled,
}: {
  onSettle: () => void;
  onRestart: () => void;
  settled: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400">
        Preview
      </span>
      {!settled ? (
        <button
          type="button"
          onClick={onSettle}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-indigo-500/20 transition-colors hover:bg-indigo-700"
        >
          Finish Run
        </button>
      ) : (
        <button
          type="button"
          onClick={onRestart}
          className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-[#475569] transition-colors hover:bg-[#f1f5f9] dark:text-white/60 dark:hover:bg-white/[0.04]"
        >
          <RotateCcw className="size-3.5" />
          Replay
        </button>
      )}
    </div>
  );
}

/**
 * Page chrome shared by both console routes: the app's standard breadcrumb
 * and title block, with the console rendered in-flow beneath so the sidebar
 * and top nav stay visible.
 */
function ConsolePageShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <PageShell className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() =>
            navigate("/continuous-compliance/governance/content-registry")
          }
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Content Registry
        </button>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm font-medium">{title}</span>
      </div>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between">
        <div>
          <h1>{title}</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            {description}
          </p>
        </div>
      </div>

      {children}
    </PageShell>
  );
}

const SAMPLE_DOC = {
  documentTitle: "MAS TRM Guidelines 2021",
  documentType: "Standard",
  fileName: "MAS TRM Guidelines 2021.pdf",
  fileSize: "2.4 MB",
} as const;

/** 2D console preview route. */
export function ProcessingConsolePage() {
  const { phase, runKey, settle, restart } = usePreviewRun();

  return (
    <ConsolePageShell
      title="Processing Console"
      description="Live view of the knowledge pipeline that runs while a document is validated"
    >
      <ProcessingConsole
        key={runKey}
        inline
        {...SAMPLE_DOC}
        phase={phase}
        topBarExtra={
          <PreviewControls
            onSettle={settle}
            onRestart={restart}
            settled={phase !== "running"}
          />
        }
      />
    </ConsolePageShell>
  );
}

/** three.js console preview route. */
export function ProcessingConsole3DPage() {
  const { phase, runKey, settle, restart } = usePreviewRun();

  return (
    <ConsolePageShell
      title="Processing Console 3D"
      description="The same pipeline rendered as a three.js scene, with each stage orbiting the RegO AI core"
    >
      <ProcessingConsole3D
        key={runKey}
        inline
        {...SAMPLE_DOC}
        phase={phase}
        topBarExtra={
          <PreviewControls
            onSettle={settle}
            onRestart={restart}
            settled={phase !== "running"}
          />
        }
      />
    </ConsolePageShell>
  );
}
